import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import React from 'react';
import {
  type CatalogProperty,
  PropertyPortalPublicationConnectionState,
  type PropertyPortalPublication,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { type TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import {
  getPropertyPublicationActivityTimestamp,
  getPropertyPublicationModeDisplay,
  getPropertyPublicationOriginDisplay,
  getPropertyPublicationStatusDisplay,
  getPropertyPublicationTypeDisplay,
} from '../../utils/property-publication-display.util';
import { PropertyPublicationInfoBadge } from '../molecules/property-publication-info-badge';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import {
  buildPropertyPublicationActions,
  propertyPublicationActionIconByKind,
  renderPropertyPublicationExternalCell,
  renderPropertyPublicationPortalAvatar,
  renderPropertyPublicationPropertyCell,
} from '../../utils/property-publications-table.util';

export interface PropertyPublicationsTableProps {
  publications: PropertyPortalPublication[];
  loading: boolean;
  propertyById: Map<number, CatalogProperty | null>;
  /** Property IDs whose existing detail requests are pending. */
  loadingPropertyIds?: ReadonlySet<number>;
  isMeliConnected: boolean;
  isMeliSyncMode: boolean;
  onOpenProperty: (propertyId: number) => void;
  onManagePublication: (publicationId: number) => void;
}

export const PropertyPublicationsTable: React.FC<
  PropertyPublicationsTableProps
> = ({
  publications,
  loading,
  propertyById,
  loadingPropertyIds,
  isMeliConnected,
  isMeliSyncMode,
  onOpenProperty,
  onManagePublication,
}) => {
  const { t } = useI18n();
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canReadPublicationsForActions } = useHasPermissions(
    IntegrationPermission.Read
  );
  const { hasPermission: canPublishAll } = useHasPermissions(
    PropertyPermission.Publish
  );
  const { hasPermission: canPublishScoped } = useHasPermissions(
    PropertyPermission.PublishScoped
  );
  const { user } = useCurrentUser();
  const canReadProperties = canReadAll || canReadScoped;

  const columns: TableColumnProps<PropertyPortalPublication>[] = [
    {
      header: t('catalog.properties.publications.table.code'),
      accessor: 'propertyId',
      className: 'whitespace-nowrap',
      render: (_value, publication) => (
        <Text size="small" weight="medium" className="whitespace-nowrap">
          <PropertyCode
            loading={loadingPropertyIds?.has(publication.propertyId)}
            displayCode={propertyById.get(publication.propertyId)?.displayCode}
            fallback={`#${publication.propertyId}`}
          />
        </Text>
      ),
    },
    {
      header: t('catalog.properties.publications.table.property'),
      accessor: 'property',
      className: 'min-w-[280px]',
      render: (_value, publication) =>
        renderPropertyPublicationPropertyCell({
          publication,
          propertyById,
          canReadProperties,
          t,
          onOpenProperty,
        }),
    },
    {
      header: t('catalog.properties.publications.table.operation'),
      accessor: 'operation',
      render: (_value, publication) => (
        <Text size="small">
          {publication.operation
            ? t(
                `catalog.properties.publications.operation.${publication.operation}`
              )
            : t('catalog.properties.publications.operation.unknown')}
        </Text>
      ),
    },
    {
      header: t('catalog.properties.publications.table.portal'),
      accessor: 'portal',
      render: (_value, publication) =>
        renderPropertyPublicationPortalAvatar(publication.portal),
    },
    {
      header: '',
      accessor: 'externalListingId',
      className: 'whitespace-nowrap',
      render: (_value, publication) =>
        renderPropertyPublicationExternalCell({
          publication,
          canReadPublicationsForActions,
          t,
        }),
    },
    {
      header: t('catalog.properties.publications.table.status'),
      accessor: 'status',
      render: (_value, publication) =>
        publication.connectionState ===
        PropertyPortalPublicationConnectionState.REFERENCE_ONLY ? null : (
          <PropertyPublicationInfoBadge
            display={getPropertyPublicationStatusDisplay({ publication })}
            size="md"
          />
        ),
    },
    {
      header: t('catalog.properties.publications.table.mode'),
      accessor: 'recordMode',
      render: (_value, publication) => (
        <PropertyPublicationInfoBadge
          display={getPropertyPublicationModeDisplay(publication)}
          size="md"
        />
      ),
    },
    {
      header: t('catalog.properties.publications.table.type'),
      accessor: 'connectionState',
      render: (_value, publication) => (
        <PropertyPublicationInfoBadge
          display={getPropertyPublicationTypeDisplay(publication)}
          size="md"
        />
      ),
    },
    {
      header: t('catalog.properties.publications.table.origin'),
      accessor: 'originProvider',
      render: (_value, publication) => (
        <PropertyPublicationInfoBadge
          display={getPropertyPublicationOriginDisplay(publication)}
          size="md"
        />
      ),
    },
    {
      header: t('catalog.properties.publications.table.lastActivity'),
      accessor: 'updatedAt',
      className: 'whitespace-nowrap',
      render: (_value, publication) => (
        <Text size="small">
          {formatDate(getPropertyPublicationActivityTimestamp(publication))}
        </Text>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      className: 'w-[56px]',
      render: (_value, publication) => {
        const property = propertyById.get(publication.propertyId);
        const canManagePublications =
          canPublishAll ||
          (canPublishScoped && user != null && property?.userId === user.id);
        const actions = buildPropertyPublicationActions({
          publication,
          canManagePublications,
          canDeletePublications: canManagePublications,
          isMeliConnected,
          isMeliSyncMode,
          t,
          onManagePublication,
        });

        if (actions.length === 0) {
          return null;
        }

        const dropdownOptions: TagDropdownOption[] = actions.map((action) => ({
          id: action.key,
          content: (
            <Text
              size="small"
              color={action.variant === 'danger' ? 'danger' : 'secondary'}
            >
              {action.label}
            </Text>
          ),
          icon: propertyPublicationActionIconByKind[action.kind],
        }));

        return (
          <TagDropdown
            content={
              <div className="flex items-center">
                <PiDotsThreeVertical className="h-4 w-4" />
              </div>
            }
            variant="primary"
            appearance="soft"
            size="sm"
            placement="right-start"
            options={dropdownOptions}
            onOptionSelect={(option) => {
              const selectedAction = actions.find(
                (action) => action.key === option.id
              );
              selectedAction?.onClick();
            }}
          />
        );
      },
    },
  ];

  return (
    <Table<PropertyPortalPublication>
      variant="neutral"
      columns={columns}
      data={publications}
      keyExtractor={(publication) => publication.id.toString()}
      isLoading={loading}
      emptyStateMessage={t('catalog.properties.publications.empty.title')}
      emptyStateDescription={t(
        'catalog.properties.publications.empty.description'
      )}
    />
  );
};
